import { useTranslation } from 'react-i18next';
import { useGetStatsQuery, useMeQuery } from '@platform/state/api';
import { useLabel } from '@platform/hooks/useLabel';
import { cx } from '@platform/core/lib/cx';
import { routes } from '@platform/core/lib/routes';
import { MenuHeader } from '@platform/engine/menu/MenuHeader';
import { MenuLinksGroup } from '@platform/engine/menu/MenuLinksGroup';
import { catalogueLinksOf, designLinksOf, settingsLinksOf } from '@platform/engine/menu/menuLinks';
import { mainRecordTypeOf } from '@platform/engine/menu/moduleSections';
import { useMenuOpenState } from '@platform/engine/menu/useMenuOpenState';
import { useDesignCounts } from '@platform/features/modules/design/useDesignCounts';
import { useModuleDefinition } from '@platform/modularity/useModuleDefinition';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { catalogLabel, moduleNav, navGroup } from '@platform/navigation';
import { useAppSelector } from '@platform/state/hooks';
import { selectUi } from '@platform/state/selectors';
import type { ISearchMenuProps } from './ISearchMenuProps';
import type { IOrderedSummary } from './IOrderedSummary';
import { useRecentModules } from './navigator/recentModules';
import { useShellNav } from './useShellNav';
import '@platform/engine/menu/menu.css';

/**
 * Ліве меню сторінки пошуку (прототип v4 cm = m || recent[0] || ws.modules[0]: пошук лишає меню модуля — шапку «категорія · модуль ·
 * N записів» і групи «Налаштування», «Проєктування»): модуль, відкритий останнім у цьому просторі (чипи «Нещодавні»), інакше перший модуль
 * простору (порядок каталогу — у парламенті й комітеті «Справи»). Рядок над назвою — категорія модуля («Комітети» для «Комітетів ВРУ»).
 */
export function SearchMenu({ ws }: ISearchMenuProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const { data: me } = useMeQuery();
  const { catalog } = useShellNav(ws);
  const recent = useRecentModules(me?.id, ws);
  const installed = (catalog?.modules ?? []).filter((m) => m.installed);
  const first = [...installed].sort((a, b) => ((a as IOrderedSummary).order ?? Number.MAX_SAFE_INTEGER) - ((b as IOrderedSummary).order ?? Number.MAX_SAFE_INTEGER))[0];
  const moduleKey = recent.find((k) => installed.some((m) => m.key === k)) ?? first?.key ?? '';
  const strings = useModuleStrings(moduleKey);
  const { data: module } = useModuleDefinition(moduleKey);
  const openState = useMenuOpenState(moduleKey);
  const designCounts = useDesignCounts(ws, module);
  const collapsed = useAppSelector((s) => selectUi(s).leftMenuCollapsed);
  const main = module ? mainRecordTypeOf(module) : undefined;
  const { data: stats } = useGetStatsQuery({ ws, rt: main?.key ?? '', by: 'status', params: {} }, { skip: !main });
  const asideClass = cx('po-menu mnav', collapsed && 'is-collapsed');
  if (!module || !main) return <aside className={asideClass} aria-label={t('menu.nav')} />;
  const name = strings.moduleName(module.module.name);
  const group = navGroup(moduleNav(moduleKey).group);
  const eyebrow = (group && label(catalogLabel(group))) || ((strings.lang === 'uk' ? module.module.categoryUk : null) ?? module.module.category);
  const settings = [...settingsLinksOf(moduleKey, (k) => t(`settings.${k}`)), ...catalogueLinksOf(moduleKey, module, strings)];
  return (
    <aside className={asideClass} aria-label={t('menu.nav')}>
      <nav className="po-menu__nav mnav__nav" aria-label={name}>
        <MenuHeader eyebrow={eyebrow} title={name} meta={stats ? t('modules.records', { count: stats.total }) : ''} />
        <MenuLinksGroup id="settings" title={t('menu.settings')} links={settings} base={routes.moduleSettings(moduleKey)} openState={openState} />
        <MenuLinksGroup id="design" title={t('menu.design')} links={designLinksOf(moduleKey, designCounts, (k) => t(`design.${k}`))}
          base={routes.moduleDesign(moduleKey)} openState={openState} />
      </nav>
    </aside>
  );
}
